import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import {
  buildFallbackImageFromCDN,
  getProductImageAspectRatioString,
  getProductImageSizeHeight,
  getProductImageSizeString,
  getProductImageSizeWidth,
  PRODUCT_IMAGE_ANGLE_TYPES,
} from '../../utils/images';

const ProductCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: ${(props) => props.$aspectRatio};
`;

const StyledSkeletonLine = styled(SkeletonLine)`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transition: opacity 0.5s;
  opacity: ${($isLoading) => ($isLoading ? 1 : 0)};
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.5s;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
`;

const ProductCardImageFromImageService = ({
  imageServiceUrl,
  swatchLabel,
  showPlusSizeImage = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(true);

  const imageServiceParams = new URLSearchParams();
  imageServiceParams.set('model', showPlusSizeImage ? 'p' : 'd'); // p = plus, d = default
  imageServiceParams.set('imgSize', getProductImageSizeString(isMobile));
  const imageServiceParamString = imageServiceParams.toString();

  const primaryImageUrl = `https://${imageServiceUrl}?${imageServiceParamString}&version=${PRODUCT_IMAGE_ANGLE_TYPES.PRIMARY}`;
  const hoverImageUrl = `https://${imageServiceUrl}?${imageServiceParamString}&version=${PRODUCT_IMAGE_ANGLE_TYPES.SECONDARY}`;

  const handleImageLoaded = (e) => {
    setIsLoading(false);
  };

  const handleImageError = (e) => {
    const fallbackImageUrl = buildFallbackImageFromCDN({
      requestedSize: getProductImageSizeString(isMobile),
    });

    // Infinite loop occurs if the fallback image also fails to load
    // We need to make sure the fallback is only set once
    if (e.target.src !== fallbackImageUrl) {
      e.target.onerror = null; // prevents looping
      e.target.src = fallbackImageUrl;
    }
  };

  // Preload the hover image
  useEffect(() => {
    const image = new Image();
    image.src = hoverImageUrl;
  }, [hoverImageUrl]);

  return (
    <ProductCardImageContainer
      $aspectRatio={getProductImageAspectRatioString(isMobile)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledSkeletonLine
        $isLoading={isLoading}
        $height={getProductImageSizeHeight(isMobile)}
        $width={getProductImageSizeWidth(isMobile)}
      />
      <StyledImage
        $isLoading={isLoading}
        src={isHovered ? hoverImageUrl : primaryImageUrl}
        alt={swatchLabel}
        title={swatchLabel}
        onError={handleImageError}
        onLoad={handleImageLoaded}
        data-autotag={isHovered ? 'product-image-on-hover' : 'product-image'}
      />
    </ProductCardImageContainer>
  );
};

ProductCardImageFromImageService.propTypes = {
  imageServiceUrl: PropTypes.string.isRequired,
  showPlusSizeImage: PropTypes.bool,
  swatchLabel: PropTypes.string,
};

export default ProductCardImageFromImageService;
